import React, { useEffect, useRef, useState } from 'react';
import styled, { ThemedStyledProps, css } from 'styled-components';

const Button = styled.button<{ onClick: (e: any) => void; disabled?: boolean }>`
  background-color: transparent;
  border-radius: 32px;
  border-width: 2px;
  border-color: ${props => (props.disabled ? '#ccc' : '#666')};
  border-style: solid;
  width: 25px;
  height: 25px;
  color: ${props => (props.disabled ? '#ccc' : '#666')};
  font-size: 16px;
  cursor: pointer;
  font-family: Roboto, sans-serif;
  display: flex;
`;

const BaseNumField = styled.input<{ disabled?: boolean }>`
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  -moz-appearance: textfield;

  text-align: center;
  border: 0px;
  border-bottom: 1px solid ${props => (props.disabled ? '#ccc' : '#666')};
  text-weight: bold;
  font-size: 16px;
  margin: 0px 10px;
  width: 50px;
  padding: 3px;
  background-color: transparent;
  color: ${props => (props.disabled ? '#ccc' : '#666')};
`;

interface NumFieldProps {
  value: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  onChange?: (value: number) => void;
}

/**
 * Numeric stepper input with manual typing support.
 * @param props - Current value, bounds, and change callback.
 * @returns Increment/decrement control with editable input.
 */
const NumField = ({ value, min, max, disabled, onChange }: NumFieldProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const isFocused = useRef(false);
  const [displayValue, setDisplayValue] = useState<string>(String(value));

  // Keep temporary user input aligned when state is changed from outside this control.
  // Skip sync while the input is focused so the user can finish typing multi-digit numbers
  // (e.g. "50") without the parent's immediate correction overwriting the display mid-keystroke.
  useEffect(() => {
    if (!isFocused.current) {
      setDisplayValue(String(value));
    }
  }, [value]);

  const lower = min || 0;
  const clamp = (next: number) => (max != null ? Math.min(max, next) : next);

  const onClick = (inc: boolean) => {
    const currentValue = displayValue === '' ? lower : parseInt(displayValue) || lower;
    if (inc) {
      onChange && onChange(clamp(currentValue + 1));
    } else {
      onChange && onChange(currentValue !== lower ? currentValue - 1 : lower);
    }
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Button disabled={disabled} onClick={(e) => {
        e.stopPropagation();
        onClick(false)
      }}>
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}>-</div>
      </Button>
      <BaseNumField
        type="number"
        ref={ref}
        disabled={disabled}
        placeholder={String(min || 0)}
        onFocus={() => {
          isFocused.current = true;
          ref.current?.select();
        }}
        onChange={e => {
          const val = e.target.value;
          setDisplayValue(val);
          if (val !== '') {
            const newVal = parseInt(val);
            if (!isNaN(newVal) && newVal >= lower) {
              onChange && onChange(clamp(newVal));
            }
          }
        }}
        onBlur={() => {
          isFocused.current = false;
          setDisplayValue(String(value)); // sync display with corrected value
          if (displayValue === '') {
            onChange && onChange(min || 0);
          }
        }}
        value={displayValue}
      />
      <Button disabled={disabled} onClick={(e) => {
        e.stopPropagation();
        onClick(true)
      }}>
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}>+</div>
      </Button>
    </div>
  );
};

export default NumField;
